import { useState } from 'react'
import { useSearchParams, Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/AuthContext'
import Logo from '../components/Logo'

export default function Login() {
  const [error, setError] = useState('')
  const { user, signOut, signInWithGoogle, signInWithFacebook } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo') || '/'

  async function handleGoogle() {
    try {
      // Save returnTo so we can redirect after OAuth callback
      if (returnTo !== '/') localStorage.setItem('fp_return_to', returnTo)
      // Mark this as a fresh sign-in so App.jsx routes us through the
      // entry gate after the OAuth round-trip lands back on '/'.
      localStorage.setItem('fp_oauth_pending', '1')
      await signInWithGoogle()
    } catch (err) {
      setError(err.message)
    }
  }

  async function handleFacebook() {
    try {
      if (returnTo !== '/') localStorage.setItem('fp_return_to', returnTo)
      localStorage.setItem('fp_oauth_pending', '1')
      await signInWithFacebook()
    } catch (err) {
      setError(err.message)
    }
  }

  // Authenticated visitors still see the page (so they can sign out from
  // here if they want) but we surface a clear "you're already signed in"
  // notice rather than forcing a redirect. Requested in the R4 brief.
  if (user) {
    return (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center px-8" style={{ background: 'var(--fp-bg)' }}>
        <div className="w-full max-w-sm text-center">
          <div className="mb-8">
            <Logo size="lg" />
          </div>
          <div
            className="fp-card mb-6 text-left"
            style={{
              borderColor: 'var(--fp-gold)',
              background: 'var(--fp-gold-soft)',
              padding: '24px',
            }}
          >
            <div
              className="text-[11px] fp-gold-text uppercase font-semibold mb-2"
              style={{ letterSpacing: '0.25em' }}
            >
              Already signed in
            </div>
            <p className="text-sm text-white m-0" style={{ lineHeight: '1.55' }}>
              You're signed in as{' '}
              <span className="font-semibold">{user.email}</span>.
            </p>
          </div>
          <button
            onClick={() => navigate('/')}
            className="fp-btn fp-btn-primary"
          >
            Go to your dashboard
          </button>
          <button
            onClick={async () => { await signOut(); navigate('/login') }}
            className="block mx-auto mt-6 text-sm font-semibold bg-transparent border-none cursor-pointer"
            style={{ color: 'var(--fp-text-muted)' }}
          >
            Sign out
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-[100dvh] flex flex-col items-center justify-center px-8" style={{ background: 'var(--fp-bg)' }}>
      <div className="w-full max-w-sm">
        {/* Logo + tagline */}
        <div className="text-center mb-12">
          <Logo size="lg" />
          <p className="text-[var(--fp-text-muted)] text-base mt-4" style={{ fontFamily: 'var(--fp-font-body)' }}>
            Someone has to decide.
          </p>
        </div>

        {error && (
          <div className="fp-card text-sm mb-8" style={{ borderColor: 'var(--fp-red)', color: '#ff8a8a', background: 'rgba(200, 16, 46, 0.1)' }}>
            {error}
          </div>
        )}

        {/* OAuth buttons first — the quick path */}
        <div className="flex flex-col gap-3 mb-8">
          <button
            onClick={handleGoogle}
            className="w-full flex items-center justify-center gap-3 py-4 px-6 rounded-2xl text-base font-semibold cursor-pointer border transition-all hover:brightness-110"
            style={{
              background: '#fff',
              color: '#333',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>
          <button
            onClick={handleFacebook}
            className="w-full flex items-center justify-center gap-3 py-4 px-6 rounded-2xl text-base font-semibold cursor-pointer border transition-all hover:brightness-110"
            style={{
              background: '#1877F2',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            Continue with Facebook
          </button>
        </div>

        <p className="text-center text-sm mt-8" style={{ color: 'var(--fp-text-muted)' }}>
          New here?{' '}
          <Link to={`/signup${returnTo !== '/' ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`} className="font-semibold no-underline" style={{ color: 'var(--fp-red)' }}>
            Create an account
          </Link>
        </p>

        <div className="flex justify-center gap-4 mt-6 text-xs" style={{ color: 'var(--fp-text-dim)' }}>
          <Link to="/privacy" className="underline hover:opacity-80">Privacy Policy</Link>
          <span>&middot;</span>
          <Link to="/terms" className="underline hover:opacity-80">Terms of Service</Link>
        </div>
      </div>
    </div>
  )
}
